import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Message, Persona, SourceInfo, ViewMode, ChatSession, ConfidenceLevel, QueryType } from '../types';
import { chatHistoryApi } from '../services/chatHistoryApi';

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

interface ChatState {
  // Current session
  sessionId: string;
  messages: Message[];
  persona: Persona;

  // UI State
  isLoading: boolean;
  loadingStatus: string;
  viewMode: ViewMode;
  isSidebarOpen: boolean;

  // Sources
  selectedSource: SourceInfo | null;
  showSourcePanel: boolean;

  // Ripgrep panel
  showRipgrepPanel: boolean;

  // Input suggestion (for populating input field without auto-sending)
  suggestedInput: string;

  // RFP subsection — persists between messages so the user doesn't re-type it
  rfpSubsection: string;

  // History
  sessions: ChatSession[];

  // Sync state
  isInitialized: boolean;
  isSyncing: boolean;
  syncError: string | null;

  // Actions
  addMessage: (message: Omit<Message, 'id' | 'timestamp'>) => string;
  updateMessage: (id: string, updates: Partial<Message>) => void;
  setPersona: (persona: Persona) => void;
  setLoading: (isLoading: boolean, status?: string) => void;
  setViewMode: (mode: ViewMode) => void;
  setSelectedSource: (source: SourceInfo | null) => void;
  toggleSourcePanel: () => void;
  toggleRipgrepPanel: () => void;
  toggleSidebar: () => void;
  clearMessages: () => void;
  startNewSession: () => void;
  loadSession: (sessionId: string) => void;
  setMessageFeedback: (messageId: string, feedback: 'positive' | 'negative' | 'need_more_detail' | null) => void;
  clearAllSessions: () => void;
  deleteSession: (sessionId: string) => void;
  setSuggestedInput: (text: string) => void;
  setRfpSubsection: (text: string) => void;
  sidebarWidth: number;
  setSidebarWidth: (width: number) => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Sync actions
  initializeFromBackend: () => Promise<void>;
  syncSessionToBackend: (sessionId: string) => Promise<void>;
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      // Initial state
      sessionId: generateId(),
      messages: [],
      persona: 'client',
      isLoading: false,
      loadingStatus: '',
      viewMode: 'full',
      isSidebarOpen: true,
      selectedSource: null,
      showSourcePanel: false,
      showRipgrepPanel: false,
      suggestedInput: '',
      rfpSubsection: '',
      sessions: [],
      sidebarWidth: 296,
      sidebarCollapsed: false,
      isInitialized: false,
      isSyncing: false,
      syncError: null,

      // Actions
      addMessage: (message) => {
        const id = generateId();
        const newMessage: Message = {
          ...message,
          id,
          timestamp: new Date(),
        };

        // Debug logging for assistant placeholder creation
        if (message.role === 'assistant') {
          console.log('[ChatStore] Creating assistant message placeholder:', {
            id,
            isLoading: message.isLoading,
            isStreaming: message.isStreaming,
            contentLength: message.content?.length || 0,
          });
        }

        // Check if this is the first message (for title update)
        const isFirstMessage = get().messages.length === 0;

        set((state) => ({
          messages: [...state.messages, newMessage],
        }));

        // Sync to backend (fire and forget)
        // Skip sync for placeholder/loading messages (empty content fails backend validation)
        const shouldSync = message.content && message.content.trim().length > 0 && !message.isLoading && !message.isStreaming;
        if (shouldSync) {
          const state = get();

          // Lazily create backend session on first real message
          const ensureBackendSession = async () => {
            if (isFirstMessage && message.role === 'user') {
              try {
                const title = message.content.slice(0, 100).trim() || 'New Chat';
                const newSession = await chatHistoryApi.createSession({
                  persona: state.persona,
                  title,
                });
                // Update sessionId to match backend
                set((s) => ({
                  sessionId: newSession.id,
                  sessions: [
                    {
                      id: newSession.id,
                      title,
                      messages: [],
                      createdAt: new Date(),
                      updatedAt: new Date(),
                    },
                    ...s.sessions,
                  ],
                }));
                return newSession.id;
              } catch (err) {
                console.warn('Failed to create backend session:', err);
                return state.sessionId;
              }
            }
            return state.sessionId;
          };

          ensureBackendSession().then((activeSessionId) => {
            chatHistoryApi.addMessage(activeSessionId, {
              role: message.role,
              content: message.content,
              confidence: message.confidence,
              confidence_level: message.confidenceLevel,
              sources: message.sources,
              query_type: message.queryType,
            }).catch((err) => {
              console.warn('Failed to sync message to backend:', err);
            });
          });
        }

        return id;
      },

      updateMessage: (id, updates) => {
        // Get the current message before updating
        const currentMessage = get().messages.find((msg) => msg.id === id);

        set((state) => ({
          messages: state.messages.map((msg) =>
            msg.id === id ? { ...msg, ...updates } : msg
          ),
        }));

        // Sync to backend when an assistant message is finalized (was loading/streaming, now is not)
        // This handles the case where placeholder messages are updated with actual content
        const wasPlaceholder = currentMessage && (currentMessage.isLoading || currentMessage.isStreaming);
        const isNowFinalized = updates.isLoading === false || updates.isStreaming === false;
        const hasContent = updates.content && updates.content.trim().length > 0;

        // Debug logging for sync issues
        if (currentMessage?.role === 'assistant' && hasContent) {
          console.log('[ChatStore] Assistant message update:', {
            messageId: id,
            wasPlaceholder,
            isNowFinalized,
            hasContent,
            willSync: wasPlaceholder && isNowFinalized,
          });
        }

        if (wasPlaceholder && isNowFinalized && hasContent && currentMessage.role === 'assistant') {
          const state = get();
          console.log('[ChatStore] Syncing assistant message to backend, sessionId:', state.sessionId);
          chatHistoryApi.addMessage(state.sessionId, {
            role: 'assistant',
            content: updates.content!,
            confidence: updates.confidence,
            confidence_level: updates.confidenceLevel,
            sources: updates.sources,
            query_type: updates.queryType,
          }).then(() => {
            console.log('[ChatStore] Assistant message synced successfully');
          }).catch((err) => {
            console.error('[ChatStore] Failed to sync assistant message to backend:', err);
          });
        }
      },

      setPersona: (persona) => set({ persona }),

      setLoading: (isLoading, status = '') =>
        set({ isLoading, loadingStatus: status }),

      setViewMode: (viewMode) => set({ viewMode }),

      setSelectedSource: (selectedSource) =>
        set({ selectedSource, showSourcePanel: selectedSource !== null }),

      toggleSourcePanel: () =>
        set((state) => ({ showSourcePanel: !state.showSourcePanel })),

      toggleRipgrepPanel: () =>
        set((state) => ({
          showRipgrepPanel: !state.showRipgrepPanel,
          // Close source panel when opening ripgrep to avoid clutter
          showSourcePanel: state.showRipgrepPanel ? state.showSourcePanel : false,
        })),

      toggleSidebar: () =>
        set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),

      clearMessages: () => set({ messages: [] }),

      startNewSession: async () => {
        const state = get();

        // Save current session to history if it has messages
        if (state.messages.length > 0) {
          const currentSession: ChatSession = {
            id: state.sessionId,
            title: state.messages[0]?.content.slice(0, 50) || 'New Chat',
            messages: state.messages,
            createdAt: new Date(state.messages[0]?.timestamp || new Date()),
            updatedAt: new Date(),
          };
          set((s) => ({
            sessions: [currentSession, ...s.sessions.filter(sess => sess.id !== state.sessionId)].slice(0, 50),
          }));
        }

        // Use a local-only session ID; backend session will be created on first message
        set({
          sessionId: generateId(),
          messages: [],
          selectedSource: null,
          showSourcePanel: false,
        });
      },

      loadSession: async (sessionId) => {
        const state = get();

        // Save current session first
        if (state.messages.length > 0 && state.sessionId !== sessionId) {
          const currentSession: ChatSession = {
            id: state.sessionId,
            title: state.messages[0]?.content.slice(0, 50) || 'New Chat',
            messages: state.messages,
            createdAt: new Date(state.messages[0]?.timestamp || new Date()),
            updatedAt: new Date(),
          };
          // Update the previous session IN PLACE — never reorder here.
          // Prepending bubbled the just-left chat to the top of the sidebar
          // on every selection, so browsing the list scrambled the list
          // (reported 2026-08-16). Recency reordering belongs to actual
          // activity (new messages), not to viewing; a session not yet in
          // the list (brand-new unsaved chat) is still prepended.
          set((s) => ({
            sessions: s.sessions.some((sess) => sess.id === state.sessionId)
              ? s.sessions.map((sess) => (sess.id === state.sessionId ? currentSession : sess))
              : [currentSession, ...s.sessions],
          }));
        }

        // Try to load from backend first
        try {
          const sessionData = await chatHistoryApi.getSession(sessionId);
          const messages: Message[] = sessionData.messages.map((m) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            timestamp: new Date(m.created_at),
            confidence: m.confidence ?? undefined,
            confidenceLevel: (m.confidence_level as ConfidenceLevel) ?? undefined,
            sources: m.sources ?? undefined,
            queryType: (m.query_type as QueryType) ?? undefined,
            feedbackGiven: m.feedback ?? undefined,
          }));

          set({
            sessionId: sessionData.session.id,
            messages,
            selectedSource: null,
            showSourcePanel: false,
          });
        } catch (err) {
          // Fallback to local session
          console.warn('Failed to load session from backend, using local:', err);
          const session = state.sessions.find((s) => s.id === sessionId);
          if (session) {
            set({
              sessionId: session.id,
              // Finalize stale placeholders: a message persisted with
              // isLoading/isStreaming true (e.g. a send that failed) would
              // otherwise render as an eternal "Generating response…".
              messages: session.messages.map((m) =>
                m.isLoading || m.isStreaming
                  ? {
                      ...m,
                      isLoading: false,
                      isStreaming: false,
                      content: m.content || '*No response was received for this message.*',
                    }
                  : m
              ),
              selectedSource: null,
              showSourcePanel: false,
            });
          }
        }
      },

      setMessageFeedback: (messageId, feedback) => {
        set((state) => ({
          messages: state.messages.map((msg) =>
            msg.id === messageId ? { ...msg, feedbackGiven: feedback } : msg
          ),
        }));

        // Sync to backend
        chatHistoryApi.updateMessageFeedback(messageId, feedback).catch((err) => {
          console.warn('Failed to sync feedback to backend:', err);
        });
      },

      clearAllSessions: async () => {
        // Clear in backend
        try {
          await chatHistoryApi.clearAllSessions();
        } catch (err) {
          console.warn('Failed to clear sessions in backend:', err);
        }

        // Use a local-only session ID; backend session will be created on first message
        set({
          sessions: [],
          sessionId: generateId(),
          messages: [],
          selectedSource: null,
          showSourcePanel: false,
        });
      },

      deleteSession: async (sessionIdToDelete) => {
        const state = get();

        // Delete from backend
        try {
          await chatHistoryApi.deleteSession(sessionIdToDelete);
        } catch (err) {
          console.warn('Failed to delete session from backend:', err);
        }

        // If deleting current session, start a new one with local-only ID
        if (state.sessionId === sessionIdToDelete) {
          set({
            sessions: state.sessions.filter(s => s.id !== sessionIdToDelete),
            sessionId: generateId(),
            messages: [],
            selectedSource: null,
            showSourcePanel: false,
          });
        } else {
          set({
            sessions: state.sessions.filter(s => s.id !== sessionIdToDelete),
          });
        }
      },

      setSuggestedInput: (text) => set({ suggestedInput: text }),
      setRfpSubsection: (text) => set({ rfpSubsection: text }),


      setSidebarWidth: (width) => set({ sidebarWidth: Math.max(200, Math.min(500, width)) }),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

      // Sync actions
      initializeFromBackend: async () => {
        const state = get();
        if (state.isInitialized || state.isSyncing) return;

        set({ isSyncing: true, syncError: null });

        try {
          // Fetch sessions from backend
          const backendSessions = await chatHistoryApi.getSessions({ limit: 50 });

          // Filter out empty sessions (no messages) and convert to local format
          const nonEmptySessions = backendSessions.filter((s) => s.message_count > 0);

          const sessions: ChatSession[] = nonEmptySessions.map((s) => ({
            id: s.id,
            title: s.title,
            messages: [], // Messages loaded on demand
            createdAt: new Date(s.created_at),
            updatedAt: new Date(s.updated_at),
          }));

          // Delete empty sessions from backend in the background
          const emptySessions = backendSessions.filter((s) => s.message_count === 0);
          for (const emptySession of emptySessions) {
            chatHistoryApi.deleteSession(emptySession.id).catch((err) => {
              console.warn('Failed to delete empty session:', err);
            });
          }

          // Don't create a backend session here; it will be created on first message
          set({
            sessions,
            isInitialized: true,
            isSyncing: false,
          });
        } catch (err) {
          console.warn('Failed to initialize from backend, using local storage:', err);
          set({
            isInitialized: true,
            isSyncing: false,
            syncError: err instanceof Error ? err.message : 'Unknown error',
          });
        }
      },

      syncSessionToBackend: async (sessionId) => {
        const state = get();
        const session = state.sessions.find(s => s.id === sessionId);
        if (!session) return;

        try {
          // Update session title in backend
          await chatHistoryApi.updateSession(sessionId, { title: session.title });
        } catch (err) {
          console.warn('Failed to sync session to backend:', err);
        }
      },
    }),
    {
      name: 'linerdocs-chat-storage',
      partialize: (state) => ({
        sessions: state.sessions,
        sessionId: state.sessionId,
        messages: state.messages,
        persona: state.persona,
        viewMode: state.viewMode,
        sidebarWidth: state.sidebarWidth,
        rfpSubsection: state.rfpSubsection,
        sidebarCollapsed: state.sidebarCollapsed,
      }),
    }
  )
);

// Initialize from backend on app start
if (typeof window !== 'undefined') {
  // Delay initialization to allow store to hydrate from localStorage first
  setTimeout(() => {
    useChatStore.getState().initializeFromBackend();
  }, 100);
}
